import { apiDelete, apiFetch, apiPost } from "./api";

export interface TicketRow {
  id: number;
  number: string;
  title: string;
  queue_id: number;
  queue: string;
  state: string;
  state_category: string;
  created_at: string;
  changed_at: string;
  customer_key: string | null;
  customer_user: string | null;
  article_count: number;
  owner?: string | null;
}

export interface TicketPage {
  items: TicketRow[];
  total: number;
}

export interface StateFacet {
  id: number;
  name: string;
  category: string;
  tickets: number;
}

export interface OwnerFacet {
  id: number;
  name: string;
  tickets: number;
}

export interface QueueNode {
  id: number;
  name: string;
  full_name: string;
  parent_id: number | null;
  depth: number;
  is_valid: boolean;
  tickets: number;
}

export interface Attachment {
  id: number;
  filename: string;
  content_type: string;
  size: number;
  is_inline: boolean;
  has_content: boolean;
}

export interface Article {
  id: number;
  seq: number;
  sender_kind: string;
  channel: string;
  is_internal: boolean;
  from_addr: string | null;
  to_addrs: string | null;
  cc_addrs: string | null;
  subject: string | null;
  created_at: string;
  body_text: string;
  has_html: boolean;
  text_issues: string | null;
  attachments: Attachment[];
}

export interface HistoryEntry {
  id: number;
  event_type: string;
  raw_name: string | null;
  created_at: string;
  created_by: number | null;
}

export interface Customer {
  login: string;
  first_name: string;
  last_name: string;
  customer_key: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
}

export interface TicketDetail {
  id: number;
  number: string;
  title: string;
  queue: string;
  state: string;
  state_category: string;
  priority: string;
  owner: string | null;
  customer_key: string | null;
  customer_user: string | null;
  customer: Customer | null;
  created_at: string;
  changed_at: string;
  closed_at: string | null;
  articles: Article[];
  history: HistoryEntry[] | null;
}

export interface SearchItem extends TicketRow {
  article_id: number;
  subject: string | null;
  snippet: string;
  matches: number;
}

export interface SearchPage {
  items: SearchItem[];
  total: number;
  terms: string[];
  exact: string | null;
}

export type TicketSort = "created_desc" | "created_asc" | "changed_desc" | "number_desc";

export interface TicketFilters {
  queue?: number;
  subqueues?: boolean;
  states?: number[];
  q?: string;
  customer?: string;
  owner?: number;
  from?: string;
  to?: string;
  sort?: TicketSort;
}

/** Page sizes offered, besides "all" (loaded in chunks of the largest size). */
export const LIST_SIZES = [25, 50, 100, 250, 500] as const;
export const SEARCH_SIZES = [25, 50, 100] as const;

function query(params: Record<string, string | number | boolean | number[] | undefined>): string {
  const out = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    if (Array.isArray(value)) value.forEach((v) => out.append(key, String(v)));
    else out.set(key, String(value));
  }
  return out.toString();
}

export interface ArticleHtml {
  html: string;
  blocked_remote: number;
  missing_inline: number;
}

export interface SavedFilter {
  id: string;
  name: string;
  query: string;
}

export const meApi = {
  savedFilters: () => apiFetch<SavedFilter[]>("/me/saved-filters"),
  saveFilter: (name: string, query: string) => apiPost<SavedFilter>("/me/saved-filters", { name, query }),
  deleteFilter: (id: string) => apiDelete(`/me/saved-filters/${id}`),
  favorites: () => apiFetch<TicketRow[]>("/me/favorites"),
  addFavorite: (number: string) =>
    apiFetch<void>(`/me/favorites/${encodeURIComponent(number)}`, { method: "PUT" }),
  removeFavorite: (number: string) => apiDelete(`/me/favorites/${encodeURIComponent(number)}`),
  recent: () => apiFetch<TicketRow[]>("/me/recent"),
};

/** Where a ticket was opened from, so its page can go back there and step to the previous/next ticket. */
export interface TicketTrail {
  from?: string;
  numbers?: string[];
}

export const ticketsApi = {
  articleHtml: (articleId: number) => apiFetch<ArticleHtml>(`/articles/${articleId}/html`),
  list: (filters: TicketFilters, offset: number, limit: number) =>
    apiFetch<TicketPage>(
      `/tickets?${query({
        queue_id: filters.queue,
        subqueues: filters.queue !== undefined ? filters.subqueues !== false : undefined,
        state_id: filters.states,
        q: filters.q,
        customer: filters.customer,
        owner_id: filters.owner,
        created_from: filters.from,
        created_to: filters.to,
        sort: filters.sort,
        limit,
        offset,
      })}`,
    ),
  facets: () => apiFetch<StateFacet[]>("/tickets/facets"),
  owners: () => apiFetch<OwnerFacet[]>("/tickets/owners"),
  queues: () => apiFetch<QueueNode[]>("/queues"),
  ticket: (number: string) => apiFetch<TicketDetail>(`/tickets/${encodeURIComponent(number)}`),
  search: (
    q: string,
    offset: number,
    limit: number,
    options: { subjectOnly?: boolean; from?: string; to?: string; owner?: number } = {},
  ) =>
    apiFetch<SearchPage>(
      `/search?${query({
        q,
        subject_only: options.subjectOnly || undefined,
        owner_id: options.owner,
        created_from: options.from,
        created_to: options.to,
        limit,
        offset,
      })}`,
    ),
  downloadUrl: (attachmentId: number) => `/api/attachments/${attachmentId}/download`,
};

/** Read the list filters from the address bar, so a filtered list can be bookmarked and shared. */
export function filtersFromParams(params: URLSearchParams): TicketFilters {
  const number = (key: string) => {
    const value = params.get(key);
    return value && /^\d+$/.test(value) ? Number(value) : undefined;
  };
  const sort = params.get("sort");
  return {
    queue: number("queue"),
    subqueues: params.get("subqueues") !== "0",
    states: params
      .getAll("state")
      .filter((s) => /^\d+$/.test(s))
      .map(Number),
    q: params.get("q") ?? undefined,
    customer: params.get("customer") ?? undefined,
    owner: number("owner"),
    from: params.get("from") ?? undefined,
    to: params.get("to") ?? undefined,
    sort: (["created_asc", "changed_desc", "number_desc"] as const).find((s) => s === sort) ?? "created_desc",
  };
}

export function paramsFromFilters(filters: TicketFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.queue !== undefined) params.set("queue", String(filters.queue));
  if (filters.queue !== undefined && filters.subqueues === false) params.set("subqueues", "0");
  filters.states?.forEach((s) => params.append("state", String(s)));
  if (filters.q) params.set("q", filters.q);
  if (filters.customer) params.set("customer", filters.customer);
  if (filters.owner !== undefined) params.set("owner", String(filters.owner));
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.sort && filters.sort !== "created_desc") params.set("sort", filters.sort);
  return params;
}

/**
 * Queues with `tickets` counting their sub-queues too, as the list does when a queue is chosen
 * (the API counts each queue's own tickets). A queue whose parent is not visible counts on its own.
 */
export function withSubqueueCounts(queues: QueueNode[]): QueueNode[] {
  const totals = new Map(queues.map((q) => [q.id, q.tickets]));
  const byId = new Map(queues.map((q) => [q.id, q]));
  for (const queue of queues) {
    for (let parent = queue.parent_id; parent !== null && byId.has(parent);) {
      totals.set(parent, (totals.get(parent) ?? 0) + queue.tickets);
      parent = byId.get(parent)?.parent_id ?? null;
    }
  }
  return queues.map((q) => ({ ...q, tickets: totals.get(q.id) ?? q.tickets }));
}
